import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import OfferBNPLAd from '../OfferBNPLAd'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ShareButton from 'components/Common/ShareWithFriendsButton'
import FlashOfferSummary from './FlashOfferSummary'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import { useAppSelector } from 'hooks/reduxHooks'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import {
  getCheapestSelectedPackageOptionWithDefault,
  isAvailableForPurchase,
  OfferPackageOptionView,
} from 'selectors/offerPage/offerPageSelectors'
import { shouldShowSalesUSP } from 'selectors/featuresSelectors'
import FlashHotelPropertyDetails from './FlashHotelPropertyDetails'
import ContactUSP from '../ContactUSP'
import FlashHotelUSPs from './FlashHotelUSPs'
import Divider from 'components/Luxkit/Divider'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import OfferViewsUrgencyBanner from '../OfferViewsUrgencyBanner'
import Group from 'components/utils/Group'
import OfferPageLabelGroup from '../OfferPageLabelGroup'
import OfferDescription from '../OfferDescription'
import OfferPageIntroInclusions from '../OfferPageIntroInclusions'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import TextLink from 'components/Luxkit/TextLink'
import ModalContext from 'contexts/ModalContext'
import FlashHotelOfferDescription from './FlashHotelMobileVariant/FlashHotelOfferDescription'
import FlashHotelInclusionsAndHighlightsModal from './FlashHotelMobileVariant/Modals/FlashHotelInclusionsAndHighlightsModal'
import FlashOfferIntroPricingBlock from './FlashHotelMobileVariant/FlashOfferIntroPricingBlock'
import config from 'constants/config'
import ExportMediaContentButton from 'components/Common/ExportMediaContentButton/ExportMediaContentButton'
import { buildAgentHubQuotingURL } from 'lib/agentHub/quoting'
import FlashOfferWithFlightsSummary from './FlashOfferWithFlightsSummary'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import { OFFER_VIEWS_THRESHOLD } from 'constants/offer'
import useCurrentSiteTakeover from 'hooks/useCurrentSiteTakeover'
import Heading from 'components/Luxkit/Typography/Heading'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments, OptimizelyFeatureFlags } from 'constants/optimizely'
import DreamArticleUSP from '../DreamArticleUSP'
import TextButton from 'components/Luxkit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'

const OfferIntroGrid = styled(LayoutContainer)`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${rem(12)};
  padding-top: ${rem(20)};

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(28)};
    padding-bottom: ${rem(28)};
  }

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr min-content;
    grid-gap: ${rem(16)} ${rem(48)};
  }
`

const SummaryColumn = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    flex-direction: column;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const SummaryBlock = styled.div`
  ${mediaQueryUp.tablet} {
    position: sticky;
    top: ${rem(12)};
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const HotelSummary = styled(FlashOfferSummary)`
  min-width: ${rem(308)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(382)};
  }
`

const HotelFlightsSummary = styled(FlashOfferWithFlightsSummary)`
  min-width: ${rem(382)};
`

interface Props {
  cheapestSelectedOption?: OfferPackageOptionView;
  cheapestOption: OfferPackageOptionView;
  checkIn?: string;
  offer: App.HotelOffer;
  className?: string;
  onViewPackageOptions: () => void;
  flightDestination?: App.Airport;
  shouldShowSalesPhoneUSP: boolean;
  shareModalConfig: ShareConfig;
  flightOrigin?: App.Airport;
  availableForPurchase?: boolean;
  rooms?: Array<App.RoomOccupants>;
}

function FlashHotelIntro(props: Props) {
  const {
    checkIn,
    offer,
    className,
    onViewPackageOptions,
    flightDestination,
    shouldShowSalesPhoneUSP,
    shareModalConfig,
    flightOrigin,
    cheapestOption,
    availableForPurchase,
    rooms,
  } = props
  const property = offer.property
  const showModal = useContext(ModalContext)
  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])
  const isMobileDevice = useIsMobileDevice()
  const offerPageDispatch = useOfferPageDispatch()

  const hasFlights = !!cheapestOption.flightPrice && !offer.flightCacheDisabled
  const hideNoCacheMessaging = !hasFlights && offer.bundledWithFlightsOnly
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && config.businessTraveller.currentAccountMode !== 'business'
  const showHotelPrice = !offer.bundledWithFlightsOnly || (offer.bundledWithFlightsOnly && flightsEnabled && !hideNoCacheMessaging && hasFlights)
  const showHotelPriceWithFlights = !offer.bundledWithFlightsOnly && !!flightsEnabled && !hideNoCacheMessaging && hasFlights
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isExportMediaEnabled = config.EXPORT_MEDIA_CONTENT_ENABLED
  const isAgentHubEnabled = config.agentHub.isEnabled
  const mediaExportURL = isAgentHubEnabled ? buildAgentHubQuotingURL(offer.id, { currency, region }) : undefined
  const isNewMobileMultipleRoomEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.croNewMobileMultipleRoom)
  const isNewMobileMultipleRoom = (rooms?.length ?? 0) > 1 && isMobileDevice && isNewMobileMultipleRoomEnabled

  const showHotelFlightsV2Summary = availableForPurchase && !isMobileDevice && flightsEnabled && !hideNoCacheMessaging && !offer.bundledWithFlightsOnly

  const openHighlightsAndInclusions = useCallback(async() => {
    showModal(
      <FlashHotelInclusionsAndHighlightsModal
        highlightsMarkdown={offer.highlights}
        additionalDescriptionMarkdown={offer.additionalDescription}
        tileInclusions={offer.tileInclusions}
      />,
    )
  }, [offer, showModal])

  const showViewsCount = offer.stats.views > OFFER_VIEWS_THRESHOLD

  const siteTakeover = useCurrentSiteTakeover()
  const shouldShowInspirationArticleUSP = !!useOptimizelyExperiment(OptimizelyExperiments.croDreamBlocksOnOfferPages, !!offer.dreamArticleId)

  const onSelectRoomsClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: undefined })
    if (!checkIn) {
      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'duration' })
    } else {
      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'multiRoomSelect' })
    }
  }, [checkIn, offerPageDispatch])

  return (
    <OfferIntroGrid className={className} data-testid="offer-intro">
      <Group direction="vertical" gap={20}>
        <CSSBreakpoint min="tablet">
          <OfferPageLabelGroup offer={offer} />
        </CSSBreakpoint>
        <FlashHotelPropertyDetails
          property={property}
          offer={offer}
        />
        {isMobileDevice && <>
          {showHotelPrice && <FlashOfferIntroPricingBlock showHotelPriceWithFlights={showHotelPriceWithFlights} />}
          {isNewMobileMultipleRoom && <SoftOfferPageWalledGarden>
            <TextButton kind="primary" onClick={onSelectRoomsClick}>{checkIn ? 'Select rooms' : 'Check availability'}</TextButton>
          </SoftOfferPageWalledGarden>}
          {offer.tileInclusions && offer.tileInclusions.length > 2 && <>
            <Divider kind="primary" />
            <Group direction="vertical" gap={12}>
              <Heading variant="heading3">Handpicked inclusions</Heading>
              <OfferPageIntroInclusions tileInclusionsList={offer.tileInclusions} />
            </Group>
            <TextLink size="medium" onClick={openHighlightsAndInclusions}>View all highlights & inclusions</TextLink>
          </>}
          {offer.description && <>
            <Divider kind="primary"/>
            <FlashHotelOfferDescription descriptionMarkdown={offer.description} facilitiesMarkdown={offer.facilities}/>
          </>}
        </>}
        {!isMobileDevice && <>
          {offer.description && <OfferDescription description={offer.description} />}
          <SoftOfferPageWalledGarden>
            {offer.additionalDescription && <>
              <Divider kind="primary"/>
              <MarkdownRender fontSize="medium" type="compact" content={offer.additionalDescription} />
            </>}
          </SoftOfferPageWalledGarden>
        </>}
        <SoftOfferPageWalledGarden>
          {siteTakeover?.offerPage?.usps && siteTakeover.offerPage.usps}
          <Divider kind="primary" />
          <Group direction="vertical" gap={12}>
            {isMobileDevice && <Heading variant="heading3">Why you should book with us</Heading>}
            <FlashHotelUSPs offer={offer} checkIn={checkIn} />
          </Group>
          {((shouldShowInspirationArticleUSP && !!offer.dreamArticleId) || shouldShowSalesPhoneUSP) && (<>
            <Divider kind="primary" />
            {shouldShowInspirationArticleUSP && !!offer.dreamArticleId && <DreamArticleUSP dreamArticleId={offer.dreamArticleId}/>}
            {shouldShowSalesPhoneUSP && <ContactUSP />}
          </>)}
        </SoftOfferPageWalledGarden>
      </Group>
      <SummaryColumn>
        <SummaryBlock>
          <CSSBreakpoint min="tablet">
            <Group direction="horizontal" verticalAlign="center" gap={8} horizontalAlign="end">
              <TripGuard inverse>
                <ShareModal shareConfig={shareModalConfig}>
                  <ShareButton horizontalOutdent={isExportMediaEnabled ? undefined : 'end'} />
                </ShareModal>
              </TripGuard>
              {isExportMediaEnabled && <TripGuard inverse>
                <ExportMediaContentButton href={mediaExportURL} target="_blank"/>
              </TripGuard>}
              <TripGuard>
                <ShareModal shareConfig={shareModalConfig}>
                  <ShareButton />
                </ShareModal>
              </TripGuard>
              <TripGuard>
                <BookmarkButton offer={offer} />
              </TripGuard>
              {isExportMediaEnabled && <TripGuard>
                <ExportMediaContentButton href={mediaExportURL} target="_blank"/>
              </TripGuard>}
            </Group>
          </CSSBreakpoint>
          <div>
            {showViewsCount && <OfferViewsUrgencyBanner offer={offer} />}
            {!showHotelFlightsV2Summary &&
              <HotelSummary
                onViewPackageOptions={onViewPackageOptions}
                flightDestination={flightDestination}
              />
            }
            {showHotelFlightsV2Summary &&
              <HotelFlightsSummary
                onViewPackageOptions={onViewPackageOptions}
                flightDestination={flightDestination}
              />
            }
          </div>
          <OfferBNPLAd prices={allPrices} />
        </SummaryBlock>
      </SummaryColumn>
    </OfferIntroGrid>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  shouldShowSalesPhoneUSP: shouldShowSalesUSP(appState, state.offer?.lowestPricePackage?.trackingPrice ?? 0, 1000),
  cheapestOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
  checkIn: state.checkInDate,
  offer: state.offer,
  flightOrigin: state.airport,
  availableForPurchase: isAvailableForPurchase(state, appState),
  rooms: state.rooms,
  open: !!state.activeFilter,
}), FlashHotelIntro, preventRenderWithOpenFilter)
